import { Button, Spinner } from "@mixitone/components";

import { Account, Night, RecurringSession } from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import { AppController } from "controllers/AppController";
import Form from "./Form";
import { FormController } from "./FormController";
import type { FormFieldConfig } from "./FormFieldsDefinition";
import { ModalFormFieldset } from "./FormRenderers/ModalFormRenderer";

export class SessionFormController extends FormController<
  Night,
  { club_id: string; recurring_session_id?: string },
  { recurringSession: RecurringSession }
> {
  override findModel(id: string) {
    return Night.query().find(id);
  }

  override async buildRecord(): Promise<Night> {
    return new Night({
      courts: 5,
      player_fee: 0,
      game_set_time: 15,
      warm_up_time: 10,
      club_id: this.state.attributes.club_id,
      session_id: this.state.attributes.recurring_session_id,
      auto_fill: true,
      auto_fill_mode: "doubles_only",
    });
  }

  override async onLoad() {
    this.get(AppController).registerController("session-form", this);
    this.addDependency("unregisterController", () =>
      this.get(AppController).unregisterController("session-form"),
    );

    if (this.state.attributes.recurring_session_id) {
      this.state.recurringSession = await RecurringSession.query().find(
        this.state.attributes.recurring_session_id,
      );
    } else if (this.state.newRecord) {
      const recurringSessions = await RecurringSession.query()
        .eq("club_id", this.state.attributes.club_id)
        .all();
      const todayWeekday = RecurringSession.recurringWeekdayFromDate(new Date());
      this.state.recurringSession =
        recurringSessions.find((session) => session.weekday === todayWeekday) || recurringSessions[0];
    }

    if (this.state.newRecord) {
      if (this.state.recurringSession) {
        this.state.record.setAttributes(this.state.recurringSession.nightAttributes);
      }
    }
  }

  override async saveRecord(record: Night): Promise<void> {
    record.club_id = this.state.attributes.club_id;
    if (this.state.attributes.recurring_session_id) {
      record.session_id = this.state.attributes.recurring_session_id;
    }
    record.account_id = Account.current?.id;

    if (this.state.newRecord) {
      const night = await Night.createAndSetup({
        club_id: this.state.attributes.club_id!,
        account_id: Account.current?.id,
        courts: record.courts,
        player_fee: record.player_fee,
        game_set_time: record.game_set_time,
        warm_up_time: record.warm_up_time,
        auto_fill: record.auto_fill,
        auto_fill_mode: record.auto_fill_mode,
        session_id: this.state.attributes.recurring_session_id ?? record.session_id,
      });
      this.state.record = night;
      return;
    }

    await record.save();
  }

  fields() {
    return {
      courts: {
        name: "courts",
        type: "number",
        label: "Number of courts",
        min: 1,
      },
      player_fee: {
        name: "player_fee",
        type: "number",
        label: "Player fee",
        max: 999.99,
        min: 0.0,
        step: 1.0,
      },
      warm_up_time: {
        name: "warm_up_time",
        type: "number",
        label: "Warm up time",
        helpText: "Warm up timer will start counting down when the first player checks in",
        max: 60,
        min: 0,
        step: 1,
      },
      game_set_time: {
        name: "game_set_time",
        type: "number",
        label: "Game time",
        max: 60,
        min: 1,
        step: 1,
      },
      auto_fill: {
        name: "auto_fill",
        type: "checkbox",
        label: "Auto fill",
        helpText: "Automatically fill courts with players when they register and as games are played",
      },
      auto_fill_mode: {
        name: "auto_fill_mode",
        type: "select",
        label: "Fill mode",
        helpText:
          "The mode of auto fill. Doubles only will only fill doubles courts, doubles and singles will fill doubles and singles courts, and singles only will only fill singles courts. You can still manually assign players to courts.",
        options: [
          { value: "doubles_only", label: "Doubles only" },
          { value: "doubles_and_singles", label: "Doubles and singles" },
          { value: "singles_only", label: "Singles only" },
        ],
      },
    } as const satisfies Record<string, FormFieldConfig>;
  }
}

const SessionForm: React.FC = () => {
  const controller = SessionFormController.use();
  const { saving, loading } = controller.state;
  const fields = Object.values(controller.fields());

  if (loading) return <Spinner size={64} />;

  return (
    <Form name="session">
      <ModalFormFieldset fields={fields} />

      <div className="flex items-center justify-end gap-2">
        <Button type="submit" disabled={saving} spinner={saving}>
          Start session
        </Button>
      </div>
    </Form>
  );
};

export default SessionFormController.scope(ApplicationView(SessionForm));
